import { Injectable } from '@angular/core';
import * as convertKeys from 'convert-keys';
import { ApiService } from './api.service';
import { FlexibleAsset } from '../model/flexible-asset.model';
import { DeeplinksService } from './deeplinks.service';
import { BehaviorSubject } from 'rxjs';
import { Equipment } from '../model/equipment.model';

@Injectable()
export class FlexibleAssetsService {
  private adminAssetUrl: string = '';
  flexibleAssets$ = new BehaviorSubject<FlexibleAsset[]>([]);

  constructor(private apiService: ApiService, private deeplinkService: DeeplinksService) {
    this.deeplinkService.getAssetAdminUrl().subscribe((url: string) => {
      this.adminAssetUrl = url;
    });
  }

  async getFlexibleAssets(site_id: string) {
    try {
      return await this.apiService.get(`sites/${site_id}/flexible-assets`);
    } catch (err) {
      console.log('Could not load flexible assets.', err);
    }
  }

  appendEquipmentToFlexibleAsset(site: string, equipment: any, asset: FlexibleAsset) {
    const equipmentType = equipment.equipmentType;
    asset.owner_id = site;
    switch (equipmentType) {
      case 'CONTROL_SET':
        if (equipment.controlElements) {
          equipment.controlElements.forEach((controlElement: any) => {
            const id = controlElement.id;
            asset.commands.push(id);
          });
        }
        break;
      case 'METER':
        if (equipment.points) {
          equipment.points.forEach((point: any) => {
            if (point.spaceType == 'MeasuredPoint') {
              const id = point.id;
              asset.performance.push(id);
              asset.monitors.push(id);
            }
          });
        }
        break;
      default:
        asset.includes.push(equipment.id);
        break;
    }
    return asset;
  }

  addMeterFromEquipmentGroup(meter: any, asset: FlexibleAsset, siteId: string) {
    asset.owner_id = siteId;
    if (meter.points) {
      meter.points.forEach((point: any) => {
        if (point.spaceType == 'MeasuredPoint') {
          const id = point.id;
          asset.performance = asset.performance.filter(pointId => pointId !== id);
          asset.monitors = asset.monitors.filter(pointId => pointId !== id);
          asset.submeters.push(id);
        }
      });
    }
    return asset;
  }

  removeMeterFromEquipmentGroup(meter: any, asset: FlexibleAsset, siteId: string) {
    asset.owner_id = siteId;
    if (meter.points) {
      meter.points.forEach((point: any) => {
        if (point.spaceType == 'MeasuredPoint') {
          const id = point.id;
          asset.submeters = asset.submeters.filter(pointId => pointId !== id);
          asset.monitors.push(id);
          asset.performance.push(id);
        }
      });
    }
    return asset;
  }

  async updateFlexibleAsset(body: FlexibleAsset) {
    const assetId = body.id;
    const response = await this.apiService.put(`flexible-assets/${assetId}`, body);
    return convertKeys.toCamel<any>(response);
  }

  async getAssociatedFlexibleAssets(equipment, siteId: string) {
    let response;
    try {
      response = await this.apiService.get(`sites/${siteId}/flexible-assets`);
    } catch (err) {
      console.error('Could not load associated flexible assets.', err);
      this.flexibleAssets$.next([]);
    }

    let childrenIds = this.extractChildrenIds(equipment);

    const filteredAssets = response
      .filter(asset => this.isAssetAssociated(asset, equipment.id, childrenIds))
      .map(asset => this.transformAsset(asset));

    this.flexibleAssets$.next(filteredAssets);
  }

  private extractChildrenIds(equipment): Set<string> {
    const children = equipment.points?.length ? equipment.points : equipment.controlElements ?? [];

    return new Set(children.map(item => item.id));
  }

  private isAssetAssociated(asset: FlexibleAsset, equipmentId: string, childrenIds: Set<string>): boolean {
    if (asset.includes.includes(equipmentId)) {
      return true;
    }

    if (childrenIds.size === 0) {
      return false;
    }

    const assetReferences = [...asset.monitors, ...asset.performance, ...asset.submeters, ...asset.commands];

    return assetReferences.some(id => childrenIds.has(id));
  }

  private transformAsset(asset: FlexibleAsset): FlexibleAsset {
    return {
      ...convertKeys.toCamel<FlexibleAsset>(asset),
      ref: `${this.adminAssetUrl}/details/${asset.id}/view`,
    };
  }
}
